import {
  AvailableDepositBonusesResponse,
  DepositTypeListResponseList,
  AvailableDepositBonusesList,
  UserMobileNumberDetailResponse,
  BankTransferMethod,
  DepositCreateResponse,
  UserDepositSummaryResponse,
  DepositPaymentMethodListResponseList,
  UserRecentUsedDepositTypesResponse,
  TopRecommendedTransferMethodsResponse,
} from "../models/response";
import { BLOB_IMAGE_URL } from "../../../config/appConfig";
import { BlobContainerConsts } from "../../../models/constants/blobContainerConsts";
import { useState } from "react";
import { Button, NumberInput } from "../../../components/ui";
import { addDays } from "date-fns";
import BonusInfoModal from "./BonusInfoModal";
import depositv1Service from "../services/deposit.service";
import { toastService as toast } from "../../../components/ui/toast/toast.service";
import SubmitDepositRequest from "./SubmitDepositRequest";
import {
  formatDateTime,
  formatUtcToLocalDateTime,
} from "../../../utils/dateTimeHelper";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { DepositFlow } from "../models/constants/depositFlow";
import {
  DepositMethodType,
  DepositPaymentMethod,
} from "../models/constants/depositMethodType";
import localization from "../../../config/localization";
import { useLanguage } from "../../../config/localization/LanguageProvider";
import { DeviceType } from "../../../models/constants/deviceType";
import userDeviceStore from "../../../store/device.store";

interface DepositFormProps {
  selectedDepositType: DepositTypeListResponseList;
  availableDepositBonuses: AvailableDepositBonusesResponse | null;
  userMobileDetail: UserMobileNumberDetailResponse | null;
  onBack: () => void;
  depositFlow: string;
  userDepositSummary?: UserDepositSummaryResponse | null;
  selectedDepositPaymentMethod?: DepositPaymentMethodListResponseList | null;
  userRecentUsedDepositTypes?: UserRecentUsedDepositTypesResponse | null;
  topRecommendedTransferMethods?: TopRecommendedTransferMethodsResponse | null;
  deviceType?: (typeof DeviceType)[keyof typeof DeviceType];
}

export default function CreateDepositRequest({
  selectedDepositType,
  availableDepositBonuses,
  userMobileDetail,
  onBack,
  depositFlow,
  userDepositSummary,
  selectedDepositPaymentMethod,
  userRecentUsedDepositTypes,
  topRecommendedTransferMethods,
  deviceType,
}: DepositFormProps) {
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState<string | null>(null);
  const [appliedBonusId, setAppliedBonusId] = useState<string | null>(null);
  const [selectedBonus, setSelectedBonus] =
    useState<AvailableDepositBonusesList | null>(null);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [showSubmitDeposit, setShowSubmitDeposit] = useState(false);
  const [transferTypeList, setTransferTypeList] = useState<
    BankTransferMethod[]
  >([]);
  const [depositCreateResponse, setDepositCreateResponse] =
    useState<DepositCreateResponse | null>(null);
  const [showGatewayIframe, setShowGatewayIframe] = useState(false);
  const [gatewayIframeUrl, setGatewayIframeUrl] = useState<string | null>(null);
  const [isQrDeposit, setIsQrDeposit] = useState(false);
  const [isBankTransfer, setIsBankTransfer] = useState(false);
  const [nextClick, setNextClick] = useState(false);
  const navigate = useNavigate();
  const isWebDevice = (deviceType ?? DeviceType.Web) === DeviceType.Web;
  const accessModeSource = userDeviceStore((state) => state.accessModeSource);

  const { language } = useLanguage();

  const handleAmountChange = (value: string) => {
    const num = Number(value);

    if (num === 0) {
      setAmountError(null);
      return;
    }

    if (isNaN(num)) {
      setAmountError(localization.please_enter_a_valid_number);
      return;
    }

    if (
      num < selectedDepositType?.minimumAmount ||
      num > selectedDepositType?.maximumAmount
    ) {
      setAmountError(
        `${localization.amount_between} \u20B9${selectedDepositType?.minimumAmount} ${localization.and} \u20B9${selectedDepositType?.maximumAmount} ${localization.deposit_method}`,
      );
      return;
    }

    setAmount(value);
    setAmountError(null);
  };

  const handleSuggestedAmountClick = (amt: number) => {
    const newAmount = (Number(amount) || 0) + amt;
    setAmount(newAmount.toString());
    handleAmountChange(newAmount.toString());
  };

  const handleApplyBonus = async (bonusId: string) => {
    const newBonusId = appliedBonusId === bonusId ? null : bonusId;
    setAppliedBonusId(newBonusId);

    if (!newBonusId) return;

    const response =
      await depositv1Service.validateBonusEligibility(newBonusId);
    if (response?.isbonusapply) {
      const appliedBonus = availableDepositBonuses?.details?.find(
        (b: any) => b.id === newBonusId,
      );
      if (appliedBonus) {
        setSelectedBonus(appliedBonus);
      }
    } else {
      toast.error(localization.you_cannot_apply_this_promocode);
    }
  };

  const RemoveApplyBonus = () => {
    setAppliedBonusId(null);
    setSelectedBonus(null);
  };

  const handleInfoClick = (bonus: AvailableDepositBonusesList) => {
    setSelectedBonus(bonus);
    setShowBonusModal(true);
  };

  const handleNextClick = async () => {
    if (nextClick) return;

    try {
      setNextClick(true);

      if (!amount) {
        setAmountError(localization.bad_response_amount);
        return;
      }

      if (selectedBonus) {
        if (Number(amount) < selectedBonus.minimumdepositlimit) {
          setAmountError(
            `${localization.deposit_amount_error} \u20B9${selectedBonus.minimumdepositlimit}.`,
          );
          return;
        }
      }

      const parsedTransferTypeList =
        await depositv1Service.parseBankTransferMethods(
          selectedDepositType.proofJson,
        );

      setTransferTypeList(parsedTransferTypeList);

      let resolvedUpiMethodName: string | null = null;
      if (depositFlow === DepositFlow.NewFlow) {
        if (
          selectedDepositPaymentMethod?.name?.toLowerCase() ===
          DepositPaymentMethod.Favorite
        ) {
          const matchedItem = userRecentUsedDepositTypes?.details
            ?.filter(
              (x) =>
                x.depositTypeId === selectedDepositType.id &&
                (!x.bankName ||
                  x.bankName.toLowerCase() ===
                  selectedDepositType.allowedABCBankTransfers?.bankName?.toLowerCase()),
            )
            ?.shift();

          resolvedUpiMethodName =
            matchedItem?.upiMethodName ??
            depositv1Service.resolveUpiMethodName(
              selectedDepositPaymentMethod,
              selectedDepositType,
              topRecommendedTransferMethods ?? null,
            );
        } else if (selectedDepositPaymentMethod?.ismanualcreation) {
          resolvedUpiMethodName = depositv1Service.resolveUpiMethodName(
            selectedDepositPaymentMethod,
            selectedDepositType,
            topRecommendedTransferMethods ?? null,
          );
        } else {
          resolvedUpiMethodName = selectedDepositPaymentMethod?.name ?? null;
        }
      }

      if (
        (selectedDepositType.depositPaymentMethod &&
          selectedDepositType.depositPaymentMethod === DepositMethodType.QR) ||
        (selectedDepositType.allowedABCBankTransfers &&
          selectedDepositType.allowedABCBankTransfers.allowedUpi &&
          selectedDepositType.allowedABCBankTransfers.allowedUpi.some(
            (x) =>
              x.toLowerCase() ===
              (selectedDepositPaymentMethod?.name ?? "").toLowerCase(),
          ))
      ) {
        setIsQrDeposit(true);
      }

      if (
        selectedDepositPaymentMethod?.name &&
        selectedDepositPaymentMethod?.name.toLowerCase() ===
        DepositPaymentMethod.BankTransfer
      ) {
        setIsBankTransfer(true);
      }

      const result = await depositv1Service.handleDepositNextStep(
        accessModeSource.sourceType,
        depositFlow,
        amount,
        selectedBonus,
        selectedDepositType,
        userMobileDetail,
        parsedTransferTypeList,
        userDepositSummary,
        resolvedUpiMethodName,
        isBankTransfer,
      );

      if (result.success) {
        if (result.status === "success") {
          if (result.data) {
            setDepositCreateResponse(result.data);
          } else if (result.gatewayHubRedirectUrl) {
            setGatewayIframeUrl(result.gatewayHubRedirectUrl);
            setShowGatewayIframe(true);
            return;
          }
          setShowSubmitDeposit(true);
        } else {
          return null;
        }
      } else {
        toast.error(result.message);
      }
    } finally {
      setNextClick(false);
    }
  };

  const handleCloseGatewayIframe = () => {
    setShowGatewayIframe(false);
    setGatewayIframeUrl(null);
    navigate("/deposit-under-approval");
  };

  if (showSubmitDeposit) {
    return (
      <SubmitDepositRequest
        selectedDepositType={selectedDepositType}
        onBack={onBack}
        transferTypeList={transferTypeList}
        amount={amount}
        selectedBonus={selectedBonus ?? null}
        depositFlow={depositFlow}
        depositCreateResponse={depositCreateResponse}
        isQrDeposit={isQrDeposit}
        isBankTransfer={isBankTransfer}
      />
    );
  }

  if (showGatewayIframe && gatewayIframeUrl) {
    return (
      <div className="fixed inset-0 bg-black z-[999]">
        <div className="fixed top-0 left-0 right-0 h-14 z-[60] flex items-center justify-end px-4">
          <Button
            onClick={handleCloseGatewayIframe}
            className="text-white text-2xl font-bold"
            aria-label="Close Payment"
          >
            <FontAwesomeIcon icon={faXmark} />
          </Button>
        </div>

        <iframe
          id="paymentIframe"
          src={gatewayIframeUrl}
          title="Gateway Payment"
          className="w-full h-full border-0 pt-14"
        />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="w-full flex justify-end items-center mb-3">
        <div className="flex items-center bg-[#1A1A1A] border border-[#2E2E2E] rounded-[6px] px-2 py-1 gap-2">
          <img
            src={
              selectedDepositType?.allowedABCBankTransfers?.logoUrl
                ? selectedDepositType?.allowedABCBankTransfers?.logoUrl
                : `${BLOB_IMAGE_URL}${BlobContainerConsts.Banks}/${selectedDepositType?.logoUrl}`
            }
            alt={selectedDepositType?.name}
            className="w-6 h-6 object-contain"
          />
          <p className="text-sm font-bold text-white">
            {selectedDepositType?.allowedABCBankTransfers?.bankName ??
              selectedDepositType?.accountName}
          </p>
          <Button
            onClick={onBack}
            className="text-xs font-medium bg-[#460413] text-[#FFBDCC] px-2 py-2 rounded-full hover:bg-[#8a0024] transition"
          >
            {localization.change}
          </Button>
        </div>
      </div>

      <div className="mt-10">
        <NumberInput
          label={`${localization.amount} (\u20B9)`}
          placeholder={localization.enter_amount}
          error={amountError}
          value={amount}
          maxLength={8}
          onChange={(e) => setAmount(e.target.value)}
          min={selectedDepositType?.minimumAmount}
          max={selectedDepositType?.maximumAmount}
          onInput={(e) => handleAmountChange(e.currentTarget.value)}
          onCopy={(e) => e.preventDefault()}
          onPaste={(e) => e.preventDefault()}
        />
      </div>

      <div className="grid grid-cols-4 gap-3 mt-5">
        {selectedDepositType?.amountSuggestionCsv
          ?.split(",")
          .map((a) => a.trim())
          .filter((a) => a)
          .map((amt) => (
            <Button
              key={amt}
              onClick={() => handleSuggestedAmountClick(Number(amt))}
              className="rounded-lg border border-[#2E2E2E] py-2 text-sm font-medium hover:bg-secondary bg-[#1A1A1A] transition"
            >
              {"\u20B9"}
              {amt}
            </Button>
          ))}
      </div>

      {(selectedDepositType?.englishDisclaimerInfo ||
        selectedDepositType?.hindiDisclaimerInfo) && (
          <p className="text-sm font-medium text-[#DDCD32] leading-snug mt-4 bg-[#2B2700] border border-[#625900] rounded-lg p-4 text-justify">
            {language === "en-US"
              ? selectedDepositType?.englishDisclaimerInfo
              : selectedDepositType?.hindiDisclaimerInfo}
          </p>
        )}

      {!isWebDevice && (
        <Button
          type="submit"
          className="mt-8 w-full rounded py-4 text-white bg-primary-gradient"
          onClick={handleNextClick}
        >
          <span>{localization.next}</span>
        </Button>
      )}

      {appliedBonusId && (
        <div className="mt-8">
          <div className="w-full">
            <div className="flex items-center justify-between bg-[#8B08C726] border border-[#2D1244] rounded-[6px] py-2 px-3">
              <div className="flex items-center gap-3">
                <img
                  src={"/icons/ic-applied-bonus.svg"}
                  alt="bonus"
                  className="w-10 h-10 object-cover"
                />
                <p className="text-[#F5F5F5] font-medium text-[16px] leading-[10px]">
                  {selectedBonus?.bonusname}
                </p>
              </div>
              <Button
                onClick={() => RemoveApplyBonus()}
                className="text-[#FF7C7E] text-sm font-medium"
              >
                {localization.remove}
              </Button>
            </div>
          </div>
        </div>
      )}

      {availableDepositBonuses?.details?.length ? (
        <div className="mt-8">
          <div
            className="
              grid gap-4 
              grid-cols-1
              lg:grid-cols-2
            "
          >
            {availableDepositBonuses.details.map((bonus: any) => {
              const isApplied = appliedBonusId === bonus.id;
              return (
                <div
                  key={bonus.id}
                  className={`flex gap-3 overflow-hidden pr-3 rounded-xl ${isApplied
                    ? "border-2 border-[#257767]"
                    : "border border-[#353535]"
                    }`}
                >
                  <img
                    src={
                      bonus.depositbonusimage &&
                      `${BLOB_IMAGE_URL}${BlobContainerConsts.DepositBonus}/${bonus.depositbonusimage}`
                    }
                    className="xs:w-[105px] sm:w-28 h-full object-cover"
                    alt="bonus"
                  />
                  <div className="flex flex-col justify-between w-full">
                    <p className="font-semibold text-[16px] leading-[24px] mt-2">
                      {bonus.bonusname}
                    </p>
                    <p className="flex items-center gap-1 mt-3 font-semibold xs:text-[10px] sm:text-sm  landscape:text-[10px] `leading-[10px] text-[#A9A9A9]">
                      <img
                        src={"/icons/ic-calendar.svg"}
                        alt="calendar"
                        className="xs:w-4 xs:h-4 landscape:w-4 landscape:h-4 sm:w-5 sm:h-5"
                      />
                      {isApplied
                        ? `${localization.expires}: ${formatDateTime(addDays(new Date(), bonus.bonusvalidclaimingdays), false)}`
                        : `${localization.claim_until}: ${formatUtcToLocalDateTime(bonus.todate, false)}`}
                    </p>
                    <div className="flex items-center gap-2 mt-3 mb-3">
                      <Button
                        onClick={() => handleApplyBonus(bonus.id)}
                        className={`xs:p-2 landscape:p-2 sm:px-8 sm:py-2 rounded-lg bg-secondary-gradient text-white font-medium xs:text-xs sm:text-sm flex-1`}
                      >
                        {isApplied && (
                          <img
                            src={"/icons/ic-applied.svg"}
                            alt="applied"
                            className="w-4 h-4 inline-block mr-2"
                          />
                        )}
                        {isApplied
                          ? localization.applied
                          : localization.apply_bonus}
                      </Button>
                      <img
                        src={"/icons/ic-bonus-info.svg"}
                        alt="info"
                        className="w-9 h-9"
                        onClick={() => handleInfoClick(bonus)}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {isWebDevice && (
        <div className="mt-8 flex justify-center">
          <Button
            type="submit"
            className="w-[40%] rounded py-4 text-white bg-primary-gradient"
            onClick={handleNextClick}
          >
            <span>{localization.next}</span>
          </Button>
        </div>
      )}

      {showBonusModal && selectedBonus && (
        <BonusInfoModal
          bonus={selectedBonus}
          languageCode={language}
          appliedBonusId={appliedBonusId}
          onClose={() => setShowBonusModal(false)}
          onApply={(id: string) => handleApplyBonus(id)}
        />
      )}
    </div>
  );
}
